import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { TableProps } from 'antd';
import { createSerializer, useQueryStates, parseAsInteger, parseAsString, parseAsStringLiteral } from 'nuqs';
import { debounce } from 'nuqs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PAGINATION, type Paginated } from '@pams/shared';
import type { ApiError } from '@/lib/api';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ONE HOOK BEHIND EVERY LIST PAGE (CLAUDE.md §8).
 *
 *  Not built at P0 because there were no list pages; P1a is the pass that needs it.
 *  Officers is the first consumer, and every later list page should be about eight
 *  lines because everything below is already solved here:
 *
 *    • URL state via nuqs, so a filtered view is a shareable link and the back button
 *      works;
 *    • the `sortBy` ALLOWLIST as a required option, fed by the same tuple the server's
 *      `z.enum` uses — one declaration, two consumers;
 *    • AntD's `onChange` mapped onto the query, including its three-click sort cycle;
 *    • the `{items,total,page,limit,pages}` envelope mapped onto AntD's pagination;
 *    • search debounced ONCE, in the URL layer, so there is not a second timer that can
 *      drift out of step with the first;
 *    • the page clamp for "deleted the last row on page 5".
 *
 *  ⚠️ [TYPED-BACK] AND `urlSearch` — ADDITIVE: THIS TABLE'S OWN CANONICAL QUERY STRING ('' at the
 *  defaults, else '?…'), so a list can hand its own address to the pages it opens (`?from=`,
 *  `lib/listOrigin.ts`). It is SERIALISED FROM THE HOOK'S CURRENT STATE with nuqs's
 *  `createSerializer`, over the SAME parsers, `urlKeys` and `clearOnDefault` the two
 *  `useQueryStates` calls use — so it is right in the very render the state changes, and it names
 *  this table's keys only (a prefixed table its prefixed ones, never a second table's).
 *  ⚠️ NEVER `useLocation().search` OR `window.location`: nuqs writes shallowly, so react-router's
 *  location never sees a list's filters ([ANALYTICS §5], measured), and the address bar is written on
 *  nuqs's own throttle, after the state it describes. No existing consumer reads it, so none moved.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type ServerTableParams<TSortBy extends string> = {
  page: number;
  limit: number;
  sortBy: TSortBy;
  sortOrder: 'asc' | 'desc';
  /** `''` when empty, never `undefined` — so the query key is stable. */
  search: string;
  filters: Record<string, string>;
};

/**
 * ⚠️ [C5b §6] `TPage` — ADDITIVE, AND DEFAULTED SO THE OTHER CONSUMERS DO NOT MOVE.
 *
 * A consumer whose endpoint carries a key beside the five pagination keys (`GET /arrangements`'
 * `searchHint`, CLAUDE.md §6) or whose `queryFn` tags the page with what produced it
 * (`searchedFor`) needs that shape back from `query.data`. Everything this hook itself reads —
 * `items`, `total`, `pages` — is `Paginated<TItem>`'s, which the bound guarantees.
 */
export type UseServerTableOptions<TItem, TSortBy extends string, TPage extends Paginated<TItem> = Paginated<TItem>> = {
  /**
   * THE ALLOWLIST. Required, so the hook cannot be instantiated without declaring it.
   * Pass the `*_SORT_FIELDS` tuple from `@pams/shared` — the same one the API feeds to
   * `createPaginationSchema`.
   */
  sortFields: readonly [TSortBy, ...TSortBy[]];
  defaultSortBy: TSortBy;
  defaultSortOrder?: 'asc' | 'desc';
  defaultLimit?: number;
  /** Extra URL-backed filters, by key. Values are opaque strings (ids, enum members). */
  filterKeys?: readonly string[];
  /** From the central factory ONLY — never an inline array (CLAUDE.md §8). */
  queryKey: (params: ServerTableParams<TSortBy>) => readonly unknown[];
  queryFn: (params: ServerTableParams<TSortBy>, signal: AbortSignal) => Promise<TPage>;
  rowKey?: (item: TItem) => string;
  /** Prefix for the URL keys, so two tables can share one page. */
  urlKeyPrefix?: string;
  searchDebounceMs?: number;
  enabled?: boolean;
};

export const useServerTable = <TItem, TSortBy extends string, TPage extends Paginated<TItem> = Paginated<TItem>>(
  options: UseServerTableOptions<TItem, TSortBy, TPage>,
) => {
  const {
    sortFields,
    defaultSortBy,
    defaultSortOrder = 'asc',
    defaultLimit = PAGINATION.DEFAULT_LIMIT,
    filterKeys = [],
    queryKey,
    queryFn,
    rowKey,
    urlKeyPrefix = '',
    searchDebounceMs = 300,
    enabled = true,
  } = options;

  const filterKeyList = filterKeys.join(',');

  /**
   * TWO `useQueryStates` calls, not one.
   *
   * A single map mixing `page: number` with N string filters needs an index signature to
   * type, and that signature then collides with the numeric keys. Splitting them keeps
   * both halves precisely typed with no cast — and nuqs batches writes made in the same
   * tick, so `setFilter` still produces ONE history entry and one refetch.
   */
  const baseParsers = useMemo(
    () => ({
      page: parseAsInteger.withDefault(1),
      limit: parseAsInteger.withDefault(defaultLimit),
      /**
       * `parseAsStringLiteral(sortFields)` is the CLIENT half of the server's `z.enum`.
       * A hand-edited `?sortBy=passwordHash` parses to null and falls back to the
       * default, so the URL cannot inject a sort field.
       *
       * Convenience, NOT security — the server re-validates against its own allowlist
       * regardless, and that is the control.
       */
      sortBy: parseAsStringLiteral(sortFields).withDefault(defaultSortBy),
      sortOrder: parseAsStringLiteral(['asc', 'desc'] as const).withDefault(defaultSortOrder),
      search: parseAsString.withDefault(''),
    }),
    [defaultLimit, defaultSortBy, defaultSortOrder, sortFields],
  );

  const filterParsers = useMemo(
    () => Object.fromEntries(filterKeys.map((key) => [key, parseAsString.withDefault('')])),
    // A literal tuple at every call site, so the joined string is a stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKeyList],
  );

  const prefixed = useCallback(
    (keys: string[]) =>
      urlKeyPrefix ? Object.fromEntries(keys.map((key) => [key, `${urlKeyPrefix}_${key}`])) : undefined,
    [urlKeyPrefix],
  );

  const nuqsOptions = {
    // Twelve keystrokes in a search box must not become twelve back-button steps.
    history: 'replace' as const,
    // Keeps URLs canonical: `?page=1` never appears, so two users on the same view
    // produce the same link AND the same cache key.
    clearOnDefault: true,
  };

  const baseUrlKeys = prefixed(['page', 'limit', 'sortBy', 'sortOrder', 'search']);
  const filterUrlKeys = prefixed([...filterKeys]);

  const [state, setState] = useQueryStates(baseParsers, {
    ...nuqsOptions,
    ...(baseUrlKeys ? { urlKeys: baseUrlKeys } : {}),
  });
  const [filterState, setFilterState] = useQueryStates(filterParsers, {
    ...nuqsOptions,
    ...(filterUrlKeys ? { urlKeys: filterUrlKeys } : {}),
  });

  /*
   * ⚠️ [TYPED-BACK] THE TABLE'S OWN ADDRESS, FROM THE STATE THIS RENDER READ — see the header. The
   * filter half is serialised ONTO the base half, so the order is canonical (base keys, then
   * `filterKeys` in declared order) whatever order the URL happens to hold them in. Not memoised:
   * two serialisers over a handful of keys cost less than the dependency list that would hold them.
   */
  const ownUrlSearch = createSerializer(filterParsers, {
    clearOnDefault: nuqsOptions.clearOnDefault,
    ...(filterUrlKeys ? { urlKeys: filterUrlKeys } : {}),
  })(
    createSerializer(baseParsers, {
      clearOnDefault: nuqsOptions.clearOnDefault,
      ...(baseUrlKeys ? { urlKeys: baseUrlKeys } : {}),
    })(state),
    filterState,
  );

  const params = useMemo<ServerTableParams<TSortBy>>(
    () => ({
      page: state.page,
      limit: state.limit,
      // nuqs distributes its conditional return type over a naked type parameter, which
      // widens this to `TSortBy | null` at the type level even though `withDefault`
      // guarantees a value. One cast, here, rather than at every consumer.
      sortBy: state.sortBy as TSortBy,
      sortOrder: state.sortOrder,
      search: state.search,
      filters: Object.fromEntries(Object.entries(filterState).filter(([, value]) => value !== '')),
    }),
    [state, filterState],
  );

  const query: UseQueryResult<TPage, ApiError> = useQuery({
    queryKey: queryKey(params),
    queryFn: ({ signal }) => queryFn(params, signal),
    // Paging does not blank the table; `isFetching` drives the spinner instead.
    placeholderData: keepPreviousData,
    enabled,
  });

  /**
   * "Deleted the last row on page 5, now staring at an empty table."
   *
   * Fixed once, here, for every list page that will ever exist.
   */
  const pages = query.data?.pages ?? 0;
  useEffect(() => {
    if (pages > 0 && params.page > pages) void setState({ page: pages });
  }, [pages, params.page, setState]);

  /**
   * Local state so typing is never laggy; the URL write is what gets debounced, and
   * because the query key derives from the URL, debouncing the URL debounces the fetch.
   * One mechanism, not two that can drift apart.
   *
   * The URL is still the source of truth, so the box has to follow it when it changes
   * from outside — the back button, or a pasted link. That is done by ADJUSTING STATE
   * DURING RENDER (React's documented pattern) rather than in an effect: an effect would
   * render once with the stale value and then again with the fresh one, and the user
   * would see the old text flash back for a frame.
   */
  const [searchInput, setSearchInput] = useState(state.search);
  /* [TYPED-BACK] Renamed from `urlSearch`, which is now the table's whole query string, returned below. */
  const [urlSearchTerm, setUrlSearchTerm] = useState(state.search);
  if (state.search !== urlSearchTerm) {
    setUrlSearchTerm(state.search);
    setSearchInput(state.search);
  }

  const onSearchChange = useCallback(
    (next: string) => {
      setSearchInput(next);
      void setState(
        // Page reset folded into the SAME write: two writes would mean two URL updates
        // and two fetches, which is the bug six hand-rolled copies of this had.
        { search: next, page: 1 },
        { limitUrlUpdates: debounce(searchDebounceMs) },
      );
    },
    [setState, searchDebounceMs],
  );

  const setFilter = useCallback(
    (key: string, value: string | undefined) => {
      // Batched by nuqs into one URL write with the page reset below.
      void setFilterState({ [key]: value ?? '' });
      void setState({ page: 1 });
    },
    [setFilterState, setState],
  );

  /**
   * ⚠️ [ANALYTICS §8] SEVERAL FILTERS IN ONE WRITE — ADDITIVE, `setSort`'s precedent.
   *
   * A filter can make another one meaningless: on the Audit log, choosing a different "What"
   * clears the entity id and the actions of the old kind. Two `setFilter` calls would rely on
   * nuqs batching writes made in one tick — true today, and a property of the library rather than
   * of this hook — and between them the key would describe a combination nobody asked for (the
   * old entity id under the new type, a guaranteed 404-shaped empty page). One object, one
   * `setFilterState`, one page reset. `undefined` clears a key, as `setFilter`'s does.
   */
  const setFilters = useCallback(
    (patch: Readonly<Record<string, string | undefined>>) => {
      void setFilterState(Object.fromEntries(Object.entries(patch).map(([key, value]) => [key, value ?? ''])));
      void setState({ page: 1 });
    },
    [setFilterState, setState],
  );

  /**
   * ⚠️ [C3c §8a] THE FIRST CHANGE TO THIS HOOK ACROSS TWELVE CONSUMERS, AND IT IS ADDITIVE.
   *
   * Sorting has always arrived through AntD's `onChange`, because a sorter is a column header.
   * §8b deletes the `Order Date` column — and with it the ONLY control that could reach the
   * `paDate` sort — while `arrangementGroups.ts` nests a group's members only under that sort.
   * So the sort has to be settable from a control that is not a column, and there was no way to
   * say that.
   *
   * ⚠️ IT IS `setFilter`'s SHAPE, DELIBERATELY: one nuqs write, batched with the page reset, so
   * changing the sort cannot leave the operator on page four of a different order. Every existing
   * consumer is untouched — nothing is removed and nothing changes meaning.
   */
  const setSort = useCallback(
    (nextSortBy: TSortBy, nextSortOrder: 'asc' | 'desc') => {
      void setState({ sortBy: nextSortBy, sortOrder: nextSortOrder, page: 1 });
    },
    [setState],
  );

  const onChange: NonNullable<TableProps<TItem>['onChange']> = useCallback(
    (pagination, _filters, sorter, extra) => {
      const single = Array.isArray(sorter) ? sorter[0] : sorter;
      const columnKey = String(single?.columnKey ?? single?.field ?? '');
      const nextSortBy = (sortFields as readonly string[]).includes(columnKey) ? (columnKey as TSortBy) : undefined;

      /**
       * AntD's THIRD sort click yields `order: undefined`. We snap back to the default
       * rather than sending no sort at all: an unordered Mongo query has no stable
       * order, so pagination would repeat and skip rows across pages. A correctness fix,
       * not a preference.
       */
      const nextOrder = single?.order === 'ascend' ? 'asc' : single?.order === 'descend' ? 'desc' : undefined;

      // `order: undefined` means "unsorted", so BOTH the column and the direction go back
      // to the defaults — not just the direction, which would leave the table ordered by
      // a column the header no longer shows as sorted.
      const cleared = nextOrder === undefined;

      /*
       * ⚠️ [C5c §9] ONLY A SORT ACTION MAY CHANGE THE SORT. AntD reports `order: undefined` on a
       * plain page change whenever no COLUMN carries the active sort — which is every sort chosen
       * through `setSort`'s Select — so reading the sorter here reset the operator's choice to the
       * default on page two. Paging keeps whatever sort is in force.
       */
      if (extra.action !== 'sort') {
        void setState({ page: pagination.current ?? 1, limit: pagination.pageSize ?? params.limit });
        return;
      }

      void setState({
        page: 1,
        limit: pagination.pageSize ?? params.limit,
        sortBy: cleared ? defaultSortBy : (nextSortBy ?? defaultSortBy),
        sortOrder: nextOrder ?? defaultSortOrder,
      });
    },
    [setState, sortFields, defaultSortBy, defaultSortOrder, params.limit],
  );

  const tableProps = {
    dataSource: query.data?.items ?? [],
    loading: query.isFetching,
    rowKey: rowKey ?? ((item: TItem) => (item as { id: string }).id),
    onChange,
    pagination: {
      current: params.page,
      pageSize: params.limit,
      total: query.data?.total ?? 0,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, PAGINATION.MAX_LIMIT],
      showTotal: (total: number, range: [number, number]) => `${range[0]}–${range[1]} of ${total}`,
    },
  } satisfies Pick<TableProps<TItem>, 'dataSource' | 'loading' | 'rowKey' | 'onChange' | 'pagination'>;

  return {
    params,
    tableProps,
    searchInput,
    onSearchChange,
    setFilter,
    setFilters,
    setSort,
    total: query.data?.total ?? 0,
    query,
    /** [TYPED-BACK] This table's own canonical query — `''` at the defaults, else `'?…'`. See the header. */
    urlSearch: ownUrlSearch,
  };
};
